import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Plus, Trash2, Users, Mail, Phone, Edit2, Send, X, 
  Camera, Check, RefreshCw, UserCircle, Upload, Lock, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { handlePlanError } from '../lib/planError';
import SecurityVerification, { useSecurityStatus, isSecurityVerified } from '../components/SecurityVerification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Beneficiaries() {
  const navigate = useNavigate();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState(null);
  const [resending, setResending] = useState(null);
  const fileInputRef = useRef(null);
  
  // Security verification state
  const [securityVerificationOpen, setSecurityVerificationOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const { hasPin } = useSecurityStatus();
  
  const [newBeneficiary, setNewBeneficiary] = useState({
    name: '',
    email: '',
    phone: '',
    relationship: '',
    picture: '',
    is_primary: false,
    is_secondary_primary: false
  });

  useEffect(() => {
    loadBeneficiaries();
  }, []);

  const loadBeneficiaries = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/beneficiaries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBeneficiaries(response.data);
    } catch (error) {
      console.error('Failed to load beneficiaries:', error);
      toast.error('Failed to load beneficiaries');
    } finally {
      setLoading(false);
    }
  };

  const createBeneficiary = async () => {
    if (!newBeneficiary.name || !newBeneficiary.email) {
      toast.error('Name and email are required');
      return;
    }

    const token = localStorage.getItem('session_token');
    setIsCreating(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/beneficiaries`,
        newBeneficiary,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Beneficiary added successfully! Invitation sent.');
      setNewBeneficiary({ name: '', email: '', phone: '', relationship: '', picture: '', is_primary: false, is_secondary_primary: false });
      setCreateDialogOpen(false);
      loadBeneficiaries();
    } catch (error) {
      console.error('Failed to create beneficiary:', error);
      handlePlanError(error, navigate, 'Failed to add beneficiary');
    } finally {
      setIsCreating(false);
    }
  };

  const updateBeneficiary = async () => {
    if (!editingBeneficiary) return;
    
    const token = localStorage.getItem('session_token');
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/beneficiaries/${editingBeneficiary.beneficiary_id}`,
        {
          name: editingBeneficiary.name,
          email: editingBeneficiary.email,
          phone: editingBeneficiary.phone,
          relationship: editingBeneficiary.relationship,
          picture: editingBeneficiary.picture,
          is_primary: editingBeneficiary.is_primary,
          is_secondary_primary: editingBeneficiary.is_secondary_primary
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Beneficiary updated successfully');
      setEditDialogOpen(false);
      setEditingBeneficiary(null);
      loadBeneficiaries();
    } catch (error) {
      console.error('Failed to update beneficiary:', error);
      toast.error('Failed to update beneficiary');
    }
  };

  const resendInvitation = async (beneficiaryId) => {
    const token = localStorage.getItem('session_token');
    setResending(beneficiaryId);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/beneficiaries/${beneficiaryId}/resend-invite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.email_sent) {
        toast.success('Invitation resent successfully!');
      } else {
        toast.warning('Invitation scheduled but email delivery may be delayed');
      }
      loadBeneficiaries();
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      toast.error('Failed to resend invitation');
    } finally {
      setResending(null);
    }
  };

  const deleteBeneficiary = async (beneficiaryId) => {
    const token = localStorage.getItem('session_token');

    try {
      await axios.delete(`${BACKEND_URL}/api/beneficiaries/${beneficiaryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Beneficiary removed');
      loadBeneficiaries();
    } catch (error) {
      console.error('Failed to delete beneficiary:', error);
      toast.error('Failed to remove beneficiary');
    }
  };

  const handlePictureUpload = async (e, isEditing = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      if (isEditing && editingBeneficiary) {
        setEditingBeneficiary({ ...editingBeneficiary, picture: base64 });
      } else {
        setNewBeneficiary({ ...newBeneficiary, picture: base64 });
      }
    };
    reader.readAsDataURL(file);
  };

  const openEditDialog = (beneficiary) => {
    // Check if security verification is required
    if (hasPin && !isSecurityVerified()) {
      setPendingAction({ type: 'edit', beneficiary });
      setSecurityVerificationOpen(true);
      return;
    }
    setEditingBeneficiary({ ...beneficiary });
    setEditDialogOpen(true);
  };

  const handleSecurityVerified = () => {
    setSecurityVerificationOpen(false);
    if (pendingAction) {
      if (pendingAction.type === 'edit') {
        setEditingBeneficiary({ ...pendingAction.beneficiary });
        setEditDialogOpen(true);
      }
      setPendingAction(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="inline-block h-12 w-12 rounded-full border-4 border-[#2B4C7E] border-r-transparent mb-4"
          />
          <p className="text-[#2B4C7E] font-medium">Loading beneficiaries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] to-[#EBF0F5]" data-testid="beneficiaries-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBF0F5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="rounded-full hover:bg-[#EBF0F5] flex-shrink-0">
                  <ArrowLeft className="h-5 w-5 text-[#2B4C7E]" />
                </Button>
              </Link>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] bg-clip-text text-transparent truncate">
                  Beneficiaries
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Manage who receives your legacy</p>
              </div>
            </div>
            
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full hover:opacity-90 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="add-beneficiary-btn">
                  <Plus className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
                  <span className="hidden sm:inline">Add Beneficiary</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md bg-white rounded-3xl">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-800">Add New Beneficiary</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Picture Upload */}
                <div className="flex flex-col items-center gap-3">
                  <div 
                    className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] flex items-center justify-center cursor-pointer overflow-hidden group"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {newBeneficiary.picture ? (
                      <img src={newBeneficiary.picture} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <UserCircle className="h-12 w-12 text-[#3A5C8F]" />
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={(e) => handlePictureUpload(e, false)}
                    className="hidden"
                  />
                  <p className="text-xs text-gray-500">Click to add photo (optional)</p>
                </div>
                
                <Input
                  placeholder="Name *"
                  value={newBeneficiary.name}
                  onChange={(e) => setNewBeneficiary({ ...newBeneficiary, name: e.target.value })}
                  className="rounded-xl"
                  data-testid="beneficiary-name-input"
                />
                <Input
                  placeholder="Email *"
                  type="email"
                  value={newBeneficiary.email}
                  onChange={(e) => setNewBeneficiary({ ...newBeneficiary, email: e.target.value })}
                  className="rounded-xl"
                  data-testid="beneficiary-email-input"
                />
                <Input
                  placeholder="Phone (optional)"
                  value={newBeneficiary.phone}
                  onChange={(e) => setNewBeneficiary({ ...newBeneficiary, phone: e.target.value })}
                  className="rounded-xl"
                  data-testid="beneficiary-phone-input"
                />
                <Input
                  placeholder="Relationship (optional)"
                  value={newBeneficiary.relationship}
                  onChange={(e) => setNewBeneficiary({ ...newBeneficiary, relationship: e.target.value })}
                  className="rounded-xl"
                  data-testid="beneficiary-relationship-input"
                />
                <div className="flex flex-col gap-2 p-3 bg-[#EBF0F5] rounded-xl">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newBeneficiary.is_primary}
                      onChange={(e) => setNewBeneficiary({ ...newBeneficiary, is_primary: e.target.checked, is_secondary_primary: false })}
                      className="w-4 h-4 text-[#2B4C7E] rounded"
                      data-testid="primary-checkbox"
                    />
                    <span className="text-sm font-medium text-gray-700">Primary Executor</span>
                    <span className="text-xs text-[#2B4C7E] bg-[#EBF0F5] px-2 py-0.5 rounded-full">Can trigger Easy Passing</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newBeneficiary.is_secondary_primary}
                      onChange={(e) => setNewBeneficiary({ ...newBeneficiary, is_secondary_primary: e.target.checked, is_primary: false })}
                      className="w-4 h-4 text-[#B3956B] rounded"
                      data-testid="secondary-checkbox"
                    />
                    <span className="text-sm font-medium text-gray-700">Secondary Executor</span>
                    <span className="text-xs text-[#B3956B] bg-[#F2E9D9] px-2 py-0.5 rounded-full">Backup executor</span>
                  </label>
                </div>
                <Button 
                  onClick={createBeneficiary} 
                  disabled={isCreating}
                  className="w-full bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full py-6 hover:opacity-90"
                  data-testid="submit-beneficiary-btn"
                >
                  {isCreating ? (
                    <>
                      <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="h-5 w-5 mr-2" />
                      Add & Send Invitation
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {beneficiaries.length === 0 ? (
          <motion.div 
            className="text-center py-24"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-24 h-24 bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="h-12 w-12 text-[#3A5C8F]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">No beneficiaries yet</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Add the people you trust to receive your legacy. They&apos;ll be notified and can accept their role.
            </p>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full px-8"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Your First Beneficiary
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {beneficiaries.map((beneficiary, index) => (
                <motion.div
                  key={beneficiary.beneficiary_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 rounded-2xl border-2 border-[#EBF0F5] bg-white shadow-lg hover:shadow-xl transition-all overflow-hidden">
                    {/* Header with actions */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="relative">
                        {beneficiary.picture ? (
                          <img 
                            src={beneficiary.picture} 
                            alt={beneficiary.name}
                            className="w-16 h-16 rounded-full object-cover ring-4 ring-[#EBF0F5]"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] rounded-full flex items-center justify-center ring-4 ring-[#EBF0F5]">
                            <span className="text-2xl font-bold text-[#2B4C7E]">
                              {beneficiary.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        {/* Status indicator */}
                        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${
                          beneficiary.status === 'accepted' ? 'bg-green-500' :
                          beneficiary.status === 'pending' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`} />
                      </div>
                      
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(beneficiary)}
                          className="text-[#2B4C7E] hover:text-[#1E3A63] hover:bg-[#EBF0F5] rounded-full"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        {beneficiary.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resendInvitation(beneficiary.beneficiary_id)}
                            disabled={resending === beneficiary.beneficiary_id}
                            className="text-[#2B4C7E] hover:text-[#1E3A63] hover:bg-[#EBF0F5] rounded-full"
                            title="Resend invitation"
                          >
                            {resending === beneficiary.beneficiary_id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteBeneficiary(beneficiary.beneficiary_id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full"
                          data-testid={`delete-beneficiary-${beneficiary.beneficiary_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Name */}
                    <h3 className="text-lg font-bold text-gray-800 mb-1">{beneficiary.name}</h3>
                    
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {beneficiary.relationship && (
                        <span className="px-2 py-1 bg-[#EBF0F5] text-[#1E3A63] rounded-full text-xs font-medium">
                          {beneficiary.relationship}
                        </span>
                      )}
                      {beneficiary.is_primary && (
                        <span className="px-2 py-1 bg-[#F2E9D9] text-[#836A45] rounded-full text-xs font-medium">
                          ⭐ Primary Executor
                        </span>
                      )}
                      {beneficiary.is_secondary_primary && (
                        <span className="px-2 py-1 bg-[#F2E9D9] text-[#836A45] rounded-full text-xs font-medium">
                          🔸 Secondary Executor
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        beneficiary.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        beneficiary.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {beneficiary.status === 'accepted' ? '✓ Accepted' : 
                         beneficiary.status === 'pending' ? '⏳ Pending' : 
                         '✗ Declined'}
                      </span>
                    </div>
                    
                    {/* Contact info */}
                    <div className="space-y-2 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="h-4 w-4 text-[#3A5C8F]" />
                        <span className="truncate">{beneficiary.email}</span>
                      </div>
                      {beneficiary.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="h-4 w-4 text-[#3A5C8F]" />
                          <span>{beneficiary.phone}</span>
                        </div>
                      )}
                      {['bounced', 'spam_complaint', 'failed'].includes(beneficiary.email_status) && (
                        <div
                          data-testid={`beneficiary-email-warning-${beneficiary.beneficiary_id}`}
                          className="flex items-start gap-2 text-sm rounded-xl bg-red-50 border border-red-200 text-red-800 px-3 py-2 mt-1"
                        >
                          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <p className="font-semibold">
                              {beneficiary.email_status === 'bounced' ? "Invitation couldn't be delivered"
                                : beneficiary.email_status === 'spam_complaint' ? 'Marked as spam by recipient'
                                : "Invitation email failed to send"}
                            </p>
                            <p className="text-xs text-red-700/90">
                              {beneficiary.email_status_detail || 'This email address may be wrong. Fix it and resend.'}
                            </p>
                          </div>
                        </div>
                      )}
                      {beneficiary.email_status === 'delivered' && (
                        <div className="flex items-center gap-1.5 text-xs text-green-700" data-testid={`beneficiary-email-delivered-${beneficiary.beneficiary_id}`}>
                          <Check className="h-3.5 w-3.5" /> Invitation delivered
                        </div>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md bg-white rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-800">Edit Beneficiary</DialogTitle>
          </DialogHeader>
          {editingBeneficiary && (
            <div className="space-y-4 mt-4">
              {/* Picture Upload */}
              <div className="flex flex-col items-center gap-3">
                <div 
                  className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] flex items-center justify-center cursor-pointer overflow-hidden group"
                  onClick={() => document.getElementById('edit-picture-input')?.click()}
                >
                  {editingBeneficiary.picture ? (
                    <img src={editingBeneficiary.picture} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <UserCircle className="h-12 w-12 text-[#3A5C8F]" />
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="h-6 w-6 text-white" />
                  </div>
                </div>
                <input
                  id="edit-picture-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handlePictureUpload(e, true)}
                  className="hidden"
                />
                {editingBeneficiary.picture && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingBeneficiary({ ...editingBeneficiary, picture: '' })}
                    className="text-red-500 text-xs"
                  >
                    Remove photo
                  </Button>
                )}
              </div>
              
              <Input
                placeholder="Name *"
                value={editingBeneficiary.name}
                onChange={(e) => setEditingBeneficiary({ ...editingBeneficiary, name: e.target.value })}
                className="rounded-xl"
              />
              <Input
                placeholder="Email *"
                type="email"
                value={editingBeneficiary.email}
                onChange={(e) => setEditingBeneficiary({ ...editingBeneficiary, email: e.target.value })}
                className="rounded-xl"
              />
              <Input
                placeholder="Phone (optional)"
                value={editingBeneficiary.phone || ''}
                onChange={(e) => setEditingBeneficiary({ ...editingBeneficiary, phone: e.target.value })}
                className="rounded-xl"
              />
              <Input
                placeholder="Relationship (optional)"
                value={editingBeneficiary.relationship || ''}
                onChange={(e) => setEditingBeneficiary({ ...editingBeneficiary, relationship: e.target.value })}
                className="rounded-xl"
              />
              <div className="flex flex-col gap-2 p-3 bg-[#EBF0F5] rounded-xl">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingBeneficiary.is_primary}
                    onChange={(e) => setEditingBeneficiary({ 
                      ...editingBeneficiary, 
                      is_primary: e.target.checked, 
                      is_secondary_primary: e.target.checked ? false : editingBeneficiary.is_secondary_primary 
                    })}
                    className="w-4 h-4 text-[#2B4C7E] rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Primary Executor</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingBeneficiary.is_secondary_primary}
                    onChange={(e) => setEditingBeneficiary({ 
                      ...editingBeneficiary, 
                      is_secondary_primary: e.target.checked, 
                      is_primary: e.target.checked ? false : editingBeneficiary.is_primary 
                    })}
                    className="w-4 h-4 text-[#B3956B] rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Secondary Executor</span>
                </label>
              </div>
              
              <DialogFooter className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setEditDialogOpen(false);
                    setEditingBeneficiary(null);
                  }}
                  className="rounded-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={updateBeneficiary}
                  className="bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Security Verification */}
      <SecurityVerification
        open={securityVerificationOpen}
        onClose={() => {
          setSecurityVerificationOpen(false);
          setPendingAction(null);
        }}
        onVerified={handleSecurityVerified}
        title="Verify to Edit Beneficiary"
        description="Enter your security PIN to modify beneficiary information"
      />
    </div>
  );
}
